/**
 * @name OperadorContext
 * @description Contexto para gerenciar o estado de autenticação do operador no totem
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/contexts/OperadorContext/OperadorContext.tsx
 */

import React, { createContext, useCallback, useEffect, useState } from "react";
import { supabase } from "../../../supabaseConfig";
import { useAlert } from "../../hooks/useAlert";
import { pessoasService } from "../../services/supabase/entities/PessoasService";
import { useLoadingContext } from "../LoadingContext/useLoadingContext";

// Constantes para persistência da sessão
const STORAGE_KEYS = {
  OPERADOR_SESSION: "is_cantina_operador_session",
  SESSION_TIMESTAMP: "is_cantina_session_timestamp",
} as const;

// Duração máxima da sessão em milissegundos (24 horas) - apenas para limpeza de sessões muito antigas
const MAX_SESSION_DURATION = 24 * 60 * 60 * 1000;

// Interface para dados da sessão persistida
interface IOperadorSessionData {
  operador: IOperadorTotem;
  timestamp: number;
}

/**
 * Utilitários para persistência da sessão do operador
 */
const SessionPersistence = {
  /**
   * Salva a sessão do operador no localStorage
   */
  save: (operador: IOperadorTotem): void => {
    try {
      const sessionData: IOperadorSessionData = {
        operador,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        STORAGE_KEYS.OPERADOR_SESSION,
        JSON.stringify(sessionData),
      );
      console.log("💾 Sessão do operador salva no localStorage");
    } catch (error) {
      console.error("❌ Erro ao salvar sessão:", error);
    }
  },

  /**
   * Recupera a sessão do operador do localStorage
   */
  load: (): IOperadorTotem | null => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEYS.OPERADOR_SESSION);
      if (!savedData) return null;

      const sessionData: IOperadorSessionData = JSON.parse(savedData);
      const now = Date.now();
      const sessionAge = now - sessionData.timestamp;

      // Verificar se a sessão não expirou
      if (sessionAge > MAX_SESSION_DURATION) {
        console.log("⏰ Sessão expirada, removendo...");
        SessionPersistence.clear();
        return null;
      }

      console.log("📂 Sessão do operador recuperada do localStorage");
      return sessionData.operador;
    } catch (error) {
      console.error("❌ Erro ao carregar sessão:", error);
      SessionPersistence.clear();
      return null;
    }
  },

  /**
   * Remove a sessão do localStorage
   */
  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.OPERADOR_SESSION);
      console.log("🗑️ Sessão do operador removida do localStorage");
    } catch (error) {
      console.error("❌ Erro ao limpar sessão:", error);
    }
  },

  /**
   * Verifica se existe uma sessão válida
   */
  hasValidSession: (): boolean => {
    return SessionPersistence.load() !== null;
  },
};

// Interface para dados do operador
export interface IOperadorTotem {
  id: number;
  nome: string;
  email: string;
  tipo: number; // 1, 2 ou 3 (Administrador, Coordenador, Operador)
  organizacao_id: number;
  sessao_inicio: Date;
  auth_user_id?: string; // ID do usuário no Supabase Auth
}

// Interface do contexto
export interface OperadorContextType {
  operadorAtual: IOperadorTotem | null;
  isTotemLiberado: boolean;
  isRecoveringSession: boolean;
  autenticarOperador: (email: string, senha: string) => Promise<boolean>;
  encerrarSessao: () => Promise<void>;
  prolongarSessao: () => void;
  validarSenhaOperador: (senha: string) => Promise<boolean>;
}

const OperadorContext = createContext<OperadorContextType | undefined>(
  undefined,
);

export const OperadorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { showError, showSuccess } = useAlert();
  const { setLoadingState } = useLoadingContext();

  // Estados do operador
  const [operadorAtual, setOperadorAtual] = useState<IOperadorTotem | null>(
    null,
  );
  const [isTotemLiberado, setIsTotemLiberado] = useState<boolean>(false);
  const [isRecoveringSession, setIsRecoveringSession] = useState<boolean>(true);
  const [isSessionRecovered, setIsSessionRecovered] = useState<boolean>(false);

  /**
   * Recupera a sessão do operador ao inicializar o componente APENAS UMA VEZ
   */
  useEffect(() => {
    const recuperarSessao = async () => {
      try {
        console.log("🔄 Verificando sessão salva do operador...");

        // Verificar se existe uma sessão válida no localStorage
        const operadorSalvo = SessionPersistence.load();

        if (operadorSalvo) {
          console.log("📂 Sessão encontrada, validando autenticação...");

          // Verificar se ainda há uma sessão ativa no Supabase
          const { data: session } = await supabase.auth.getSession();

          if (session?.session?.user) {
            // Verificar se o usuário da sessão corresponde ao operador salvo
            if (session.session.user.id === operadorSalvo.auth_user_id) {
              console.log(
                "✅ Sessão válida encontrada, restaurando operador:",
                operadorSalvo.nome,
              );
              setOperadorAtual(operadorSalvo);
              setIsTotemLiberado(true);
              setIsSessionRecovered(true);
              showSuccess(
                `Sessão restaurada. Bem-vindo de volta, ${operadorSalvo.nome}!`,
              );
            } else {
              console.log(
                "⚠️ Sessão não corresponde ao operador salvo, limpando...",
              );
              SessionPersistence.clear();
            }
          } else {
            console.log(
              "❌ Nenhuma sessão ativa no Supabase, limpando sessão local...",
            );
            SessionPersistence.clear();
          }
        } else {
          console.log("ℹ️ Nenhuma sessão salva encontrada");
        }
      } catch (error) {
        console.error("❌ Erro ao recuperar sessão:", error);
        SessionPersistence.clear();
      } finally {
        setIsRecoveringSession(false);
      }
    };

    recuperarSessao();
  }, []); // ⚠️ ARRAY VAZIO - executa apenas uma vez

  /**
   * Salva a sessão apenas quando necessário (não durante recuperação)
   */
  useEffect(() => {
    if (
      operadorAtual &&
      isTotemLiberado &&
      !isRecoveringSession &&
      !isSessionRecovered
    ) {
      SessionPersistence.save(operadorAtual);
    }
  }, [operadorAtual, isTotemLiberado, isRecoveringSession, isSessionRecovered]);

  // Função para autenticar operador
  const autenticarOperador = useCallback(
    async (email: string, senha: string): Promise<boolean> => {
      try {
        setLoadingState("auth-loading", true);
        console.log("🔐 Iniciando autenticação do operador:", { email });

        // Autenticação com Supabase Auth
        const { data: authData, error: authError } =
          await supabase.auth.signInWithPassword({
            email: email.trim(),
            password: senha,
          });

        if (authError) {
          console.log("❌ Erro na autenticação:", authError.message);
          showError("Email ou senha incorretos");
          return false;
        }

        if (!authData.user) {
          console.log("❌ Nenhum usuário retornado");
          showError("Erro na autenticação");
          return false;
        }

        // Buscar dados do operador na tabela pessoas
        const operadorResponse = await pessoasService.getByAuthUserId(
          authData.user.id,
        );
        const { data: operadorData, error: operadorError } = operadorResponse;

        if (operadorError || !operadorData) {
          console.log(
            "❌ Operador não encontrado na base de dados:",
            operadorError?.message,
          );
          showError("Operador não encontrado");
          // Fazer logout do Supabase se o operador não existir na nossa base
          await supabase.auth.signOut();
          return false;
        }

        // Verificar se é um operador válido (tipos 1, 2 ou 3)
        if (![1, 2, 3].includes((operadorData as any).fk_id_tipo_pessoa || 0)) {
          console.log(
            "❌ Usuário não é um operador válido:",
            (operadorData as any).fk_id_tipo_pessoa,
          );
          showError("Usuário não possui permissão para operar o totem");
          await supabase.auth.signOut();
          return false;
        }

        const operador: IOperadorTotem = {
          id: (operadorData as any).id,
          nome: (operadorData as any).nome || "Operador",
          email: (operadorData as any).email || email,
          tipo: (operadorData as any).fk_id_tipo_pessoa || 3,
          organizacao_id: (operadorData as any).fk_id_organizacao || 0,
          sessao_inicio: new Date(),
          auth_user_id: authData.user.id,
        };

        setOperadorAtual(operador);
        setIsTotemLiberado(true);
        setIsSessionRecovered(false); // Marcar que é uma nova autenticação, não recuperação

        console.log("✅ Operador autenticado:", operador.nome);
        showSuccess(`Bem-vindo, ${operador.nome}! Totem liberado para uso.`);
        return true;
      } catch (error) {
        console.error("❌ Erro na autenticação:", error);
        showError("Erro ao autenticar operador");
        return false;
      } finally {
        setLoadingState("auth-loading", false);
      }
    },
    [setLoadingState, showError, showSuccess],
  );

  // Função para encerrar sessão
  const encerrarSessao = useCallback(async () => {
    console.log("🚪 Encerrando sessão do operador");

    // Limpar dados persistidos
    SessionPersistence.clear();

    // Fazer logout do Supabase Auth
    await supabase.auth.signOut();

    setOperadorAtual(null);
    setIsTotemLiberado(false);
    setIsSessionRecovered(false);
    showSuccess("Sessão encerrada. Totem bloqueado.");
  }, [showSuccess]);

  // Função para prolongar sessão (resetar timer de inatividade)
  const prolongarSessao = useCallback(() => {
    if (operadorAtual) {
      const operadorAtualizado = {
        ...operadorAtual,
        sessao_inicio: new Date(),
      };
      // Atualizar diretamente no localStorage sem alterar o estado para evitar loops
      SessionPersistence.save(operadorAtualizado);
      // Só atualizar o estado se necessário para o timer visual
      setOperadorAtual(operadorAtualizado);
      console.log("⏰ Sessão prolongada e persistida");
    }
  }, [operadorAtual]);

  // Função para validar senha do operador atual
  const validarSenhaOperador = useCallback(
    async (senha: string): Promise<boolean> => {
      if (!operadorAtual) {
        console.log("❌ Nenhum operador logado");
        return false;
      }

      try {
        console.log("🔐 Validando senha do operador:", operadorAtual.email);

        // Verificar senha com Supabase Auth
        const { error } = await supabase.auth.signInWithPassword({
          email: operadorAtual.email,
          password: senha,
        });

        if (error) {
          console.log("❌ Senha incorreta:", error.message);
          return false;
        }

        console.log("✅ Senha validada com sucesso");
        return true;
      } catch (error) {
        console.error("❌ Erro ao validar senha:", error);
        return false;
      }
    },
    [operadorAtual],
  );

  return (
    <OperadorContext.Provider
      value={{
        operadorAtual,
        isTotemLiberado,
        isRecoveringSession,
        autenticarOperador,
        encerrarSessao,
        prolongarSessao,
        validarSenhaOperador,
      }}
    >
      {children}
    </OperadorContext.Provider>
  );
};

export default OperadorContext;
